<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useDialogPluginComponent } from 'quasar';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n';

import type { FormDto, Locales, QuestionDto } from '@/services/types/api.types';
import { useLanguageValidationErrors } from '@/composables';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { useFormsStore } from '@/stores';
import { localizableString } from '@/helpers/validation.helper';
import { Constants } from '@/constants';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import { provideEditLocale } from '@/composables/data-display-utils';

const { t } = useI18n();
const formsStore = useFormsStore();

// PROPS
const props = withDefaults(
  defineProps<{
    consultationId: number;
    sectionId: number;
    form: FormDto;
    languageEnabled: Locales[];
    isTemplate?: boolean;
  }>(),
  {
    isTemplate: false,
  },
);

// DIALOG SET-UP
defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

// FORM
const { handleSubmit, errors, meta } = useForm({
  initialValues: props.form,
  validationSchema: yup.object<FormDto>({
    question: yup.object<QuestionDto>({
      title: localizableString(props.languageEnabled),
      // todo: need to find a way to emable or not based on form type
      // content: localizableString(props.languageEnabled),
    }),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);

const editLocale = provideEditLocale(toRef(() => props.languageEnabled));
const typeOptions = computed(() => formsStore.getFormTypes());

const onSubmit = handleSubmit(async (values) => {
  if (values.type === Constants.Form.Type.Proposal) {
    values.answers = formsStore.createProposalAnswers();
  } else if (values.type === Constants.Form.Type.OpenQuestion) {
    values.answers = formsStore.createOpenQuestionAnswer();
  } else if (values.type === Constants.Form.Type.Matrix) {
    values.answers = formsStore.createMatrixAnswer();
  } else if (values.type === Constants.Form.Type.Map) {
    values.answers = formsStore.createMapAnswer();
  } else if (values.type === Constants.Form.Type.DotVoting) {
    values.answers = formsStore.createDotVotingAnswer();
  } else {
    values.answers = formsStore.createQuestionAnswer();
  }

  await wrapSavingOperation(
    async () =>
      await formsStore.saveForm(values, {
        isTemplate: props.isTemplate,
        sectionId: props.sectionId,
        consultationId: props.consultationId,
      }),
    () => onDialogOK(),
  );
});
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ t('form.dialogs.forms.create') }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-select
          name="type"
          :label="t('form.type.label')"
          :editLocale="editLocale"
          :options="typeOptions"
          emit-value
          map-options
          outlined
        />
        <localized-input
          name="question.title"
          :label="t('form.title')"
          :editLocale="editLocale"
          type="textarea"
          outlined
          :language_enabled="languageEnabled"
        />
        <localized-input
          name="question.content"
          :label="t('form.question.content')"
          :editLocale="editLocale"
          type="textarea"
          outlined
          :language_enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
